import React from "react";
import { TouchableOpacity, StyleSheet, StyleProp, ViewStyle, ActivityIndicator } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useTheme } from "@/hooks/useTheme";

type ButtonVariant = "primary" | "secondary" | "success" | "danger" | "warning" | "ghost";
type ButtonSize = "small" | "medium" | "large";

interface ActionButtonProps {
  title: string;
  onPress: () => void;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: keyof typeof MaterialCommunityIcons.glyphMap;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  style?: StyleProp<ViewStyle>;
}

export function ActionButton({
  title,
  onPress,
  variant = "primary",
  size = "medium",
  icon,
  loading = false,
  disabled = false,
  fullWidth = false,
  style,
}: ActionButtonProps) {
  const { colors } = useTheme();

  const variantStyles = {
    primary: {
      backgroundColor: colors.primary,
      textColor: "white",
      borderColor: colors.primary,
    },
    secondary: {
      backgroundColor: colors.backgroundSecondary,
      textColor: colors.text,
      borderColor: colors.border,
    },
    success: {
      backgroundColor: colors.emerald?.background || "#10b981",
      textColor: colors.emerald?.text || "white",
      borderColor: colors.emerald?.border || "#10b981",
    },
    danger: {
      backgroundColor: colors.rose?.background || "#ef4444",
      textColor: colors.rose?.text || "white",
      borderColor: colors.rose?.border || "#ef4444",
    },
    warning: {
      backgroundColor: colors.amber?.background || "#f59e0b",
      textColor: colors.amber?.text || "white",
      borderColor: colors.amber?.border || "#f59e0b",
    },
    ghost: {
      backgroundColor: "transparent",
      textColor: colors.text,
      borderColor: colors.border,
    },
  };

  const sizeStyles = {
    small: {
      paddingHorizontal: 12,
      paddingVertical: 8,
      fontSize: 12,
      iconSize: 14,
      minHeight: 32,
    },
    medium: {
      paddingHorizontal: 16,
      paddingVertical: 12,
      fontSize: 14,
      iconSize: 16,
      minHeight: 44,
    },
    large: {
      paddingHorizontal: 20,
      paddingVertical: 16,
      fontSize: 16,
      iconSize: 18,
      minHeight: 56,
    },
  };

  const variant_style = variantStyles[variant] || variantStyles.primary; // Fallback to primary if variant not found
  const size_style = sizeStyles[size] || sizeStyles.medium; // Fallback to medium if size not found

  const isDisabled = disabled || loading;

  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={isDisabled}
      style={[
        styles.button,
        {
          backgroundColor: variant_style.backgroundColor,
          borderColor: variant_style.borderColor,
          paddingHorizontal: size_style.paddingHorizontal,
          paddingVertical: size_style.paddingVertical,
          minHeight: size_style.minHeight,
          opacity: isDisabled ? 0.6 : 1,
        },
        fullWidth && styles.fullWidth,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator size="small" color={variant_style.textColor} />
      ) : (
        <>
          {icon && (
            <MaterialCommunityIcons
              name={icon}
              size={size_style.iconSize}
              color={variant_style.textColor}
              style={{ marginRight: 8 }}
            />
          )}
          <ThemedText
            style={[
              styles.buttonText,
              {
                color: variant_style.textColor,
                fontSize: size_style.fontSize,
              },
            ]}
          >
            {title}
          </ThemedText>
        </>
      )}
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  button: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.1)",
    elevation: 2,
  },
  fullWidth: {
    flex: 1,
  },
  buttonText: {
    fontWeight: "600",
    textAlign: "center",
  },
});
